/**
 * Hook customizado para gerenciar janela Picture-in-Picture
 * 
 * Este hook encapsula toda a lógica de:
 * - Detecção de suporte do navegador
 * - Abertura/fechamento da janela PIP
 * - Cópia de estilos CSS
 * - Gerenciamento de estado
 * - Fallback para navegadores sem suporte
 * 
 * @author VISION DESK
 * @version 0.24.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { UsePIPOptions, UsePIPReturn } from '@/types/pictureInPicture';
import { canUsePIPNative, logContextDiagnostics } from '@/utils/secureContext';

export const usePIP = ({ osId, sessionId, onClose, onOpen }: UsePIPOptions): UsePIPReturn => {
  // Estado da janela PIP
  const [isPIPOpen, setIsPIPOpen] = useState(false);
  const [pipWindow, setPIPWindow] = useState<Window | null>(null);
  const [isFallbackMode, setIsFallbackMode] = useState(false);
  
  // Referência para cleanup
  const cleanupRef = useRef<(() => void) | null>(null);
  
  // Detectar suporte do navegador para Document PIP API
  // Agora também verifica se está em contexto seguro (HTTPS ou localhost)
  const isPIPSupported = canUsePIPNative();

  /**
   * Copia todos os estilos CSS do documento principal para a janela PIP
   * Isso garante que a interface na janela PIP tenha a mesma aparência
   */
  const copyStyles = useCallback((sourceDoc: Document, targetDoc: Document) => {
    try {
      // DIAGNÓSTICO INICIAL
      console.log('📺 PIP DIAG | copyStyles:start', {
        sourceHref: sourceDoc.location?.href,
        targetTitle: targetDoc.title,
      });

      const cloneAttributes = (sourceEl: HTMLElement, targetEl: HTMLElement) => {
        targetEl.className = sourceEl.className;

        if (sourceEl.id) {
          targetEl.id = sourceEl.id;
        } else {
          targetEl.removeAttribute('id');
        }

        if (sourceEl.hasAttribute('style')) {
          targetEl.setAttribute('style', sourceEl.getAttribute('style') ?? '');
        } else {
          targetEl.removeAttribute('style');
        }

        // Resetar atributos data-* antes de copiar
        const targetDataset = targetEl.dataset as Record<string, string>;
        Object.keys(targetDataset).forEach((dataKey) => {
          delete targetDataset[dataKey];
        });

        // Remover atributos que não existem na origem (evita resíduos)
        Array.from(targetEl.attributes).forEach((attr) => {
          if (
            !sourceEl.hasAttribute(attr.name) &&
            !attr.name.startsWith('data-') &&
            attr.name !== 'class' &&
            attr.name !== 'id' &&
            attr.name !== 'style'
          ) {
            targetEl.removeAttribute(attr.name);
          }
        });

        const sourceDataset = sourceEl.dataset as Record<string, string>;
        Object.entries(sourceDataset || {}).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            targetDataset[key] = String(value);
          }
        });

        // Copiar demais atributos (ex: lang, dir)
        Array.from(sourceEl.attributes).forEach((attr) => {
          if (attr.name.startsWith('data-')) {
            return; // já copiado via dataset
          }
          targetEl.setAttribute(attr.name, attr.value);
        });
      };

      // Resetar head para evitar duplicações
      targetDoc.head.innerHTML = '';

      // Copiar atributos globais de <html> e <body>
      cloneAttributes(sourceDoc.documentElement as HTMLElement, targetDoc.documentElement as HTMLElement);
      cloneAttributes(sourceDoc.body as HTMLElement, targetDoc.body as HTMLElement);

      // Copiar CSS custom properties (variáveis) do :root
      const sourceRootStyles = getComputedStyle(sourceDoc.documentElement);
      let cssVarCount = 0;
      for (let i = 0; i < sourceRootStyles.length; i++) {
        const propName = sourceRootStyles[i];
        if (propName.startsWith('--')) {
          const propValue = sourceRootStyles.getPropertyValue(propName);
          targetDoc.documentElement.style.setProperty(propName, propValue);
          cssVarCount++;
        }
      }

      // Replicar todos os elementos suportados do head, preservando ordem
      let linksCloned = 0;
      let stylesCloned = 0;
      Array.from(sourceDoc.head.children).forEach((node) => {
        const tagName = node.tagName.toLowerCase();

        // Evitar executar scripts novamente dentro do PIP
        if (tagName === 'script') {
          return;
        }

        // Normalizar URLs de stylesheets para o mesmo origin (Windows/IP vs localhost)
        if (tagName === 'link') {
          const linkEl = node as HTMLLinkElement;
          if (linkEl.rel === 'stylesheet' && linkEl.href) {
            const normalizedHref = new URL(linkEl.href, sourceDoc.location.href).href;
            const newLink = targetDoc.createElement('link');
            newLink.rel = 'stylesheet';
            newLink.href = normalizedHref;
            targetDoc.head.appendChild(newLink);
            linksCloned++;
            console.log('📺 PIP DIAG | copyStyles:link', { href: normalizedHref });
            return;
          }
        }

        // Style e meta podem ser clonados sem ajustes
        const clonedNode = node.cloneNode(true) as HTMLElement;
        targetDoc.head.appendChild(clonedNode);
        if (tagName === 'style') {
          stylesCloned++;
        }
      });

      // Garantir meta viewport (alguns ambientes removem ao clonar)
      if (!targetDoc.querySelector('meta[name="viewport"]')) {
        const viewport = targetDoc.createElement('meta');
        viewport.name = 'viewport';
        viewport.content = 'width=device-width, initial-scale=1.0';
        targetDoc.head.appendChild(viewport);
      }

      console.log('📺 PIP DIAG | copyStyles:done', {
        htmlClass: targetDoc.documentElement.className,
        bodyClass: targetDoc.body.className,
        cssVarCount,
        linksCloned,
        stylesCloned,
        headChildren: targetDoc.head.childElementCount,
      });
    } catch (error) {
      console.error('❌ PIP DIAG | copyStyles:error', error);
    }
  }, []);

  /**
   * Abre a janela Picture-in-Picture
   * Usa API nativa se disponível, caso contrário usa fallback modal
   */
  const openPIP = useCallback(async () => {
    try {
      // Log de diagnósticos de contexto
      const contextInfo = logContextDiagnostics();
      
      // Se não suporta PIP nativo, usar fallback
      if (!isPIPSupported) {
        // console.log('⚠️ PIP: PIP nativo não disponível, usando fallback modal');
        // console.log('💡 Motivo:', contextInfo.recommendation);
        setIsFallbackMode(true);
        setIsPIPOpen(true);
        onOpen?.();
        return;
      }

      // Verificar se já existe uma janela PIP aberta
      if (window.documentPictureInPicture?.window) {
        // console.log('⚠️ PIP: Janela PIP já está aberta');
        return;
      }

      // console.log('🚀 PIP: Abrindo janela Picture-in-Picture...');

      // Solicitar abertura da janela PIP
      // Tamanho inicial: 326x805.5 (usuário pode redimensionar depois)
      const newPipWindow = await window.documentPictureInPicture!.requestWindow({
        width: 326,
        height: 806, // Arredondado para 806
      });

      // console.log('✅ PIP: Janela aberta com sucesso');

      // Copiar estilos do documento principal
      copyStyles(document, newPipWindow.document);
      console.log('📺 PIP DIAG | openPIP:afterCopy', {
        pipTitle: newPipWindow.document.title,
        headCount: newPipWindow.document.head.childElementCount,
        linkCount: newPipWindow.document.querySelectorAll('link[rel="stylesheet"]').length,
      });

      // Criar container para o React renderizar
      const container = newPipWindow.document.createElement('div');
      container.id = 'pip-root';
      container.className = 'h-full w-full overflow-hidden';
      newPipWindow.document.body.appendChild(container);

      // Aplicar estilo no body para remover margens
      newPipWindow.document.body.style.margin = '0';
      newPipWindow.document.body.style.padding = '0';
      newPipWindow.document.body.style.overflow = 'hidden';

      console.log('📺 PIP DIAG | openPIP:containerReady', {
        containerId: container.id,
        containerClass: container.className,
        bodyOverflow: newPipWindow.document.body.style.overflow,
      });

      // Garantir estilos de scrollbar roxa mesmo se não clonados
      const hasCustomScrollbarStyle =
        !!newPipWindow.document.querySelector('style[data-pip="custom-scrollbar"]');
      if (!hasCustomScrollbarStyle) {
        const style = newPipWindow.document.createElement('style');
        style.setAttribute('data-pip', 'custom-scrollbar');
        style.textContent = `
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(147, 51, 234, 0.5) rgba(255, 255, 255, 0.05);
          }
          .custom-scrollbar::-webkit-scrollbar { width: 8px; }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 4px;
            margin: 4px 0;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(147, 51, 234, 0.5);
            border-radius: 4px;
            border: 1px solid rgba(147, 51, 234, 0.2);
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(147, 51, 234, 0.7);
            border-color: rgba(147, 51, 234, 0.4);
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:active {
            background: rgba(147, 51, 234, 0.9);
          }
        `;
        newPipWindow.document.head.appendChild(style);
        console.log('📺 PIP DIAG | ensureStyles:custom-scrollbar:injected');
      } else {
        console.log('📺 PIP DIAG | ensureStyles:custom-scrollbar:present');
      }

      // Atualizar estado
      setPIPWindow(newPipWindow);
      setIsPIPOpen(true);
      setIsFallbackMode(false);

      // Callback de abertura
      onOpen?.();

      // Detectar quando janela PIP é fechada pelo usuário
      const handlePageHide = () => {
        // console.log('👋 PIP: Janela fechada pelo usuário');
        setIsPIPOpen(false);
        setPIPWindow(null);
        onClose?.();
      };

      newPipWindow.addEventListener('pagehide', handlePageHide);

      // Salvar cleanup function
      cleanupRef.current = () => {
        newPipWindow.removeEventListener('pagehide', handlePageHide);
      };

    } catch (error) {
      // console.error('❌ PIP: Erro ao abrir janela:', error);
      
      // Em caso de erro, tentar fallback
      // console.log('⚠️ PIP: Tentando fallback modal após erro...');
      setIsFallbackMode(true);
      setIsPIPOpen(true);
      onOpen?.();
    }
  }, [isPIPSupported, copyStyles, onOpen, onClose]);

  /**
   * Fecha a janela Picture-in-Picture
   */
  const closePIP = useCallback(() => {
    try {
      // console.log('👋 PIP: Fechando janela...');

      // Limpar event listeners
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      // Fechar janela nativa
      if (pipWindow && !isFallbackMode) {
        pipWindow.close();
      }

      // Atualizar estado
      setIsPIPOpen(false);
      setPIPWindow(null);
      setIsFallbackMode(false);

      // Callback de fechamento
      onClose?.();

      // console.log('✅ PIP: Janela fechada com sucesso');
    } catch (error) {
      // console.error('❌ PIP: Erro ao fechar janela:', error);
    }
  }, [pipWindow, isFallbackMode, onClose]);

  /**
   * Cleanup ao desmontar o componente
   */
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  /**
   * Detectar se navegador principal foi fechado mas PIP continua aberto
   * Salvar estado no localStorage para recuperar depois
   */
  useEffect(() => {
    if (isPIPOpen) {
      const pipState = {
        osId,
        sessionId,
        timestamp: Date.now(),
      };
      localStorage.setItem('pip-state', JSON.stringify(pipState));
    } else {
      localStorage.removeItem('pip-state');
    }
  }, [isPIPOpen, osId, sessionId]);

  /**
   * Recuperar estado do PIP ao recarregar página
   */
  useEffect(() => {
    const savedState = localStorage.getItem('pip-state');
    if (savedState) {
      try {
        const pipState = JSON.parse(savedState);
        const timeDiff = Date.now() - pipState.timestamp;
        
        // Se foi salvo há menos de 5 minutos, considerar válido
        if (timeDiff < 5 * 60 * 1000) {
          // console.log('🔄 PIP: Estado recuperado do localStorage');
          // Aqui você pode implementar lógica para reabrir PIP se desejar
        } else {
          localStorage.removeItem('pip-state');
        }
      } catch (error) {
        // console.error('❌ PIP: Erro ao recuperar estado:', error);
        localStorage.removeItem('pip-state');
      }
    }
  }, []);

  return {
    isPIPOpen,
    pipWindow,
    isPIPSupported,
    openPIP,
    closePIP,
    isFallbackMode,
  };
};

/**
 * Utility: Verificar se navegador suporta PIP
 */
export const checkPIPSupport = (): boolean => {
  return 'documentPictureInPicture' in window;
};

/**
 * Utility: Obter informações sobre o navegador
 */
export const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  
  const isChrome = /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor);
  const isEdge = /Edg/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  
  return {
    isChrome,
    isEdge,
    isFirefox,
    isSafari,
    supportsNativePIP: canUsePIPNative(), // Usa verificação de contexto seguro
    userAgent: ua,
  };
};
